import { Suspense, useCallback, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { NAV_PAGES, type NavPage } from '../app/navigation';
import { useConsoleApi } from '../app/ConsoleApiContext';
import { signOut } from '../api/session';
import { useSessionSnapshot } from '../lib/capability';
import { rbacRoleLabel } from '../lib/labels';
import { RouteAttemptContext } from '../lib/routeAttempt';
import { Breadcrumb, type Crumb } from './Breadcrumb';
import { ErrorBoundary } from './ErrorBoundary';
import { Icon } from './Icon';
import { Sidebar } from './Sidebar';
import { SkeletonRows } from './States';

/** Match the current pathname against the registry (plan-order, longest first). */
function resolvePage(pathname: string): NavPage | undefined {
  const clean = pathname.replace(/\/+$/, '') || '/';
  const candidates = NAV_PAGES.filter((page) => matches(page.path, clean));
  return candidates.sort((a, b) => b.path.length - a.path.length)[0];
}

function matches(pagePath: string, pathname: string): boolean {
  const target = pagePath === '' ? '/' : `/${pagePath}`;
  if (!target.includes(':')) return pathname === target;
  const parts = target.slice(1).split('/');
  const got = pathname.slice(1).split('/');
  if (parts.length !== got.length) return false;
  return parts.every((part, i) => part.startsWith(':') || part === got[i]);
}

export function AppShell() {
  const location = useLocation();
  const page = resolvePage(location.pathname);
  const crumbTrail: Crumb[] = page
    ? page.group === 'devices' && page.hidden
      ? [{ label: 'دستگاه‌ها', to: '/devices' }, { label: page.crumb }]
      : [{ label: page.crumb }]
    : [{ label: 'یافت نشد' }];

  /**
   * How many times this route has been asked for its chunk again.
   *
   * `React.lazy` caches the promise, so a rejected import is re-thrown forever and a boundary that
   * only cleared its own state could not recover. The counter is the page's way of being asked for
   * a **fresh** lazy object (see `app/lazyPage.tsx`), and it is per route: a failure on one page
   * does not make the next page re-fetch what it already has.
   */
  const [attempts, setAttempts] = useState<Record<string, number>>({});
  const attempt = page ? (attempts[page.id] ?? 0) : 0;
  const retryRoute = useCallback(() => {
    if (!page) return;
    setAttempts((previous) => ({ ...previous, [page.id]: (previous[page.id] ?? 0) + 1 }));
  }, [page]);

  return (
    <div className="flex min-h-screen bg-surface-void" data-testid="app-shell">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-50 focus:rounded-panel focus:bg-accent-bg focus:px-3 focus:py-1 focus:text-accent-ink"
      >
        پرش به محتوا
      </a>

      <aside className="hidden w-[var(--sidebar-w)] shrink-0 lg:block">
        <div className="sticky top-0 h-screen">
          <Sidebar />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-[var(--header-h)] items-center justify-between gap-4 border-b border-surface-line bg-surface-base/95 px-4 backdrop-blur">
          <div className="flex min-w-0 items-center gap-3">
            <Breadcrumb items={crumbTrail} />
            <h1 className="truncate text-ui font-semibold text-ink">
              {page?.title ?? 'کنسول عملیاتی ACS'}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <DataSourceBadge />
            <AuthBadge />
          </div>
        </header>

        <nav
          className="flex gap-1 overflow-x-auto border-b border-surface-line bg-surface-base px-2 py-1 lg:hidden"
          aria-label="ناوبری موبایل"
        >
          {NAV_PAGES.filter((p) => !p.hidden).map((p) => (
            <a
              key={p.id}
              href={p.path ? `/${p.path}` : '/'}
              className="nav-item whitespace-nowrap"
              aria-current={page?.id === p.id ? 'page' : undefined}
            >
              <Icon name={p.icon} size={14} />
              <span className="text-label">{p.crumb}</span>
            </a>
          ))}
        </nav>

        <main id="main" className="flex-1 px-4 py-4">
          <ErrorBoundary label={page?.title} onRetry={retryRoute}>
            <RouteAttemptContext.Provider value={attempt}>
              <Suspense fallback={<RouteLoading />}>
                <Outlet />
              </Suspense>
            </RouteAttemptContext.Provider>
          </ErrorBoundary>
        </main>

        <footer className="border-t border-surface-line px-4 py-2 text-caption text-ink-faint">
          کنسول عملیاتی ACS — قرارداد v1 (client assertion Ed25519، RFC 9457، صفحه‌بندی cursor).
          صفحه‌ها فقط از <span className="mono">openapi/openapi.yaml</span> مصرف می‌کنند.
        </footer>
      </div>
    </div>
  );
}

/**
 * The one fallback of the console: the shell is up, the page's chunk is not.
 *
 * It says what is happening in the console's own voice — the words `LoadingBlock` already uses
 * («در حال بارگذاری…») — and it is `role="status"`, so the operator keeps the navigation and hears
 * that the page is on its way. The shell is never replaced by it: only the page's own slot is.
 */
function RouteLoading() {
  return (
    <div className="p-panel" role="status" aria-live="polite" data-testid="route-loading">
      <p className="mb-3 text-body text-ink-dim">در حال بارگذاری این صفحه…</p>
      <SkeletonRows />
    </div>
  );
}

function DataSourceBadge() {
  const api = useConsoleApi();
  const isFixture = api.dataSource === 'fixture';
  return (
    <span
      className={`chip ${isFixture ? 'chip-stale' : 'chip-accent'}`}
      data-testid="data-source-badge"
      title={
        isFixture
          ? 'داده از fixtures داخل web/src/fixtures می‌آید؛ هیچ درخواست شبکه‌ای زده نمی‌شود.'
          : 'داده از façade زنده خوانده می‌شود.'
      }
    >
      <i className={`chip-dot ${isFixture ? 'bg-status-stale' : 'bg-accent'}`} aria-hidden="true" />
      {isFixture ? 'داده: نمونه (fixture)' : 'داده: زنده'}
    </span>
  );
}

/**
 * Who is signed in, and the way out (U-08-f).
 *
 * This badge used to state that auth was disabled and no session existed. That sentence is not
 * available any more: a person can hold a real session here, so the badge renders the identity
 * the SERVER published for it — `permissions/effective`'s `subject` and `role` first, the login
 * response only as the fallback while the mirror has not arrived. The role word is a label for
 * a wire value (§۲), never a hint of what it may do: capability levels belong to
 * `useCapability()`, and this component produces no access rule.
 */
function AuthBadge() {
  const api = useConsoleApi();
  const snapshot = useSessionSnapshot();
  // A sign-out the server refused leaves the session live, and the badge must say that instead
  // of looking logged in behind the operator's back.
  const [refused, setRefused] = useState<string | null>(null);

  const onSignOut = () => {
    setRefused(null);
    void signOut().catch((error: unknown) => {
      setRefused(error instanceof Error ? error.message : String(error));
    });
  };

  if (snapshot.status !== 'signed-in') {
    return (
      <span
        className="chip chip-neutral"
        data-testid="auth-badge"
        title={
          api.dataSource === 'http'
            ? 'بدون نشست: دروازهٔ مسیر شما را به صفحهٔ ورود می‌برد.'
            : 'حالت نمونه بدون نشست هم داده می‌دهد؛ برای دیدن صفحهٔ ورود و نشانِ هویت، وارد /login شوید.'
        }
      >
        <Icon name="shield" size={13} />
        <span>{api.dataSource === 'http' ? 'احراز: بدون نشست' : 'احراز: حالت نمونه'}</span>
        <Link className="ms-1 text-caption text-accent-ink underline" to="/login">
          ورود
        </Link>
      </span>
    );
  }

  const subject = snapshot.effective?.subject ?? snapshot.identity?.username ?? '';
  const role = snapshot.effective?.role ?? snapshot.identity?.role ?? null;
  const expires = snapshot.identity?.expiresAt ?? null;
  return (
    <div className="flex items-center gap-2" data-testid="auth-badge">
      <span
        className="chip chip-accent"
        title={`نشست ${snapshot.identity?.sid ?? ''} — انقضا: ${expires ?? 'نامعلوم'}`}
        data-testid="identity-badge"
      >
        <Icon name="shield" size={13} />
        <span className="mono max-w-[16ch] truncate" dir="ltr">
          {subject}
        </span>
        <span className="text-ink-faint">·</span>
        <span>{rbacRoleLabel(role)}</span>
      </span>
      {refused ? (
        <span
          className="chip chip-stale"
          role="alert"
          data-testid="sign-out-refused"
          title={refused}
        >
          خروج پذیرفته نشد
        </span>
      ) : null}
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={onSignOut}
        data-testid="sign-out"
      >
        خروج
      </button>
    </div>
  );
}
